import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Phone,
  PlayCircle,
  RefreshCw,
  Repeat,
  Search,
  UserX,
  Video,
  VideoOff,
  X,
  XCircle,
} from 'lucide-react';
import { db } from '../firebase';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
import CrmStatCard from '../components/crm/CrmStatCard';
import { FIELD_CLASS, FilterSelect, TH_CLASS } from '../components/crm/CrmFilterUi';
import { TASK_TIME_DEPARTMENTS, taskTimeDepartmentLabel } from '../utils/taskTimeConstants';
import {
  CALL_STATUS,
  LOG_STATUS,
  RECORDING_STATUS,
  groupCallsByAdvisor,
  groupCallsByClient,
  internalUserName,
  loadCallReport,
  needsAttention,
  summarizeCalls,
} from '../services/callReportsService';

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 75;
const COMING_UP_DAYS = 14;

const TIMEFRAMES = [
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_30', label: 'Last 30 Days' },
  { id: 'last_90', label: 'Last 90 Days' },
  { id: 'next_30', label: 'Next 30 Days' },
  { id: 'last_6m', label: 'Last 6 Months' },
  { id: 'ytd', label: 'This Year' },
  { id: 'last_12m', label: 'Last 12 Months' },
];

function getTimeframeRange(id, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (id) {
    case 'this_month':
      return { start: new Date(y, m, 1), end: new Date(y, m + 1, 1) };
    case 'last_month':
      return { start: new Date(y, m - 1, 1), end: new Date(y, m, 1) };
    case 'last_30':
      return { start: new Date(now.getTime() - 30 * DAY_MS), end: now };
    case 'next_30':
      return { start: now, end: new Date(now.getTime() + 30 * DAY_MS) };
    case 'last_6m':
      return { start: new Date(y, m - 5, 1), end: now };
    case 'ytd':
      return { start: new Date(y, 0, 1), end: now };
    case 'last_12m':
      return { start: new Date(now.getTime() - 365 * DAY_MS), end: now };
    case 'last_90':
    default:
      return { start: new Date(now.getTime() - 90 * DAY_MS), end: now };
  }
}

// Every fetch also covers the next 30 days so "Coming up" works on any timeframe.
function getFetchWindow(range, now = new Date()) {
  const ahead = new Date(now.getTime() + 30 * DAY_MS);
  return { start: range.start, end: range.end > ahead ? range.end : ahead };
}

const STATUS_META = {
  [CALL_STATUS.COMPLETED]: { label: 'Completed', cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' },
  [CALL_STATUS.UPCOMING]: { label: 'Upcoming', cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' },
  [CALL_STATUS.CANCELED]: { label: 'Canceled', cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' },
  [CALL_STATUS.RESCHEDULED]: { label: 'Rescheduled', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800' },
  [CALL_STATUS.NO_SHOW]: { label: 'No-show', cls: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800' },
};

const SOURCE_META = {
  calendly: { label: 'Calendly', cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800' },
  google: { label: 'Google Calendar', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800' },
  crm: { label: 'Hours log', cls: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700' },
  fathom: { label: 'Fathom', cls: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800' },
};

const MATCHED_BY_LABEL = {
  email: 'invitee email',
  domain: 'company email domain',
  name: 'invitee name',
  calendar: 'calendar invite',
  fathom: 'Fathom recording',
  crm: 'hours log',
};

const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';
const PILL = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtWhen(date, timeKnown = true) {
  if (!date) return '—';
  const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
  if (!timeKnown) return day;
  return `${day} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function fmtShortDate(date) {
  if (!date) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
}

function fmtMinutes(minutes) {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtHours(hours) {
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}h`;
}

function relativeDays(date, now = new Date()) {
  if (!date) return '';
  const days = Math.round((date - now) / DAY_MS);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META[CALL_STATUS.COMPLETED];
  return (
    <span className={`${PILL} ${meta.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  );
};

const LogPill = ({ call }) => {
  switch (call.logStatus) {
    case LOG_STATUS.LOGGED:
      return <span className={`${PILL} ${STATUS_META[CALL_STATUS.COMPLETED].cls}`}>Logged · {fmtHours(call.log.quantity)}</span>;
    case LOG_STATUS.NOT_LOGGED:
      return (
        <span className={`${PILL} bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800`}>
          <AlertTriangle className="h-3 w-3" />
          Not logged
        </span>
      );
    case LOG_STATUS.LOGGED_CANCELED:
      return (
        <span className={`${PILL} ${STATUS_META[CALL_STATUS.CANCELED].cls}`}>
          <AlertTriangle className="h-3 w-3" />
          Still logged
        </span>
      );
    case LOG_STATUS.UNMATCHED:
      return <span className={`${PILL} ${SOURCE_META.crm.cls}`}>No client match</span>;
    default:
      return <span className="text-xs text-gray-400">—</span>;
  }
};

const RecordingPill = ({ call }) => {
  switch (call.recordingStatus) {
    case RECORDING_STATUS.RECORDED: {
      const pill = (
        <span className={`${PILL} ${SOURCE_META.fathom.cls}`}>
          <PlayCircle className="h-3 w-3" />
          Recorded
        </span>
      );
      return call.recording?.url ? (
        <a href={call.recording.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Watch Fathom recording">
          {pill}
        </a>
      ) : pill;
    }
    case RECORDING_STATUS.NOT_RECORDED:
      return (
        <span
          className={`${PILL} ${STATUS_META[CALL_STATUS.CANCELED].cls}`}
          title={call.host?.fathomConnected === false ? `${call.host.name} hasn't connected Fathom` : 'No Fathom recording found'}
        >
          <VideoOff className="h-3 w-3" />
          Not recorded
        </span>
      );
    case RECORDING_STATUS.PENDING:
      return <span className={`${PILL} ${SOURCE_META.crm.cls}`} title="Fathom usually sends the recording within an hour of the call">Processing</span>;
    default:
      return <span className="text-xs text-gray-400">—</span>;
  }
};

const FathomNotConnectedPill = () => (
  <span className={`${PILL} bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800`}>
    Fathom not connected
  </span>
);

const SourceChips = ({ sources }) => (
  <div className="flex flex-wrap gap-1.5">
    {(sources || []).map((s) => (
      <span key={s} className={`${PILL} ${SOURCE_META[s]?.cls || SOURCE_META.crm.cls}`}>{SOURCE_META[s]?.label || s}</span>
    ))}
  </div>
);

const Avatar = ({ host, size = 'h-7 w-7' }) => {
  const name = host?.name || 'Unassigned';
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?';
  if (host?.profileImage) {
    return <img src={host.profileImage} alt="" className={`${size} shrink-0 rounded-full object-cover`} />;
  }
  return (
    <span className={`${size} shrink-0 inline-flex items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>
      {initials}
    </span>
  );
};

const HostCell = ({ host }) => (
  <div className="flex items-center gap-2 min-w-0">
    <Avatar host={host} />
    <span className="truncate text-sm text-gray-700 dark:text-gray-300">{host?.name || 'Unassigned'}</span>
  </div>
);

const IconLink = ({ href, title, icon: Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    onClick={(e) => e.stopPropagation()}
    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white transition"
  >
    <Icon className="h-4 w-4" />
  </a>
);

const CallLinks = ({ call }) => (
  <div className="flex items-center justify-end gap-0.5">
    {call.joinUrl && call.status === CALL_STATUS.UPCOMING && <IconLink href={call.joinUrl} title="Join call" icon={Video} />}
    {call.googleHtmlLink && <IconLink href={call.googleHtmlLink} title="Open in Google Calendar" icon={CalendarDays} />}
    {call.recording?.url && <IconLink href={call.recording.url} title="Watch Fathom recording" icon={PlayCircle} />}
  </div>
);

const ClientCell = ({ call, onOpenClient }) => {
  const inviteeLabel = call.invitee?.name || call.invitee?.email || '';
  if (call.accountId) {
    return (
      <div className="min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenClient(call.accountId);
          }}
          className="block max-w-full truncate text-left text-sm font-semibold text-gray-900 hover:text-red-600 dark:text-white dark:hover:text-red-400"
        >
          {call.accountName || 'Client'}
        </button>
        {inviteeLabel && <div className="truncate text-xs text-gray-500 dark:text-gray-400">{inviteeLabel}</div>}
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">{inviteeLabel || 'Unknown invitee'}</div>
      {call.invitee?.name && call.invitee?.email && (
        <div className="truncate text-xs text-gray-500 dark:text-gray-400">{call.invitee.email}</div>
      )}
    </div>
  );
};

const DetailBlock = ({ title, children }) => (
  <div className="min-w-0">
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{title}</div>
    <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">{children}</div>
  </div>
);

function logExplanation(call) {
  switch (call.logStatus) {
    case LOG_STATUS.NOT_LOGGED:
      return call.status === CALL_STATUS.UPCOMING
        ? 'Booked, but nothing is on the client\'s Implementation Hours log yet.'
        : 'This call happened but isn\'t on the client\'s Implementation Hours log, so no hours were deducted.';
    case LOG_STATUS.LOGGED_CANCELED:
      return 'This call was canceled but its log entry is still on Implementation Hours — it still counts against the client\'s hours.';
    case LOG_STATUS.UNMATCHED:
      return 'The invitee doesn\'t match any client contact, portal user or company email domain.';
    default:
      return call.kind === 'meeting' ? 'Not an Implementation Hours call type.' : 'Nothing logged (canceled calls don\'t need a log).';
  }
}

const CallDetail = ({ call }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
    <DetailBlock title="Who">
      {call.invitees.length === 0 && <div className="text-gray-500 dark:text-gray-400">No invitee details</div>}
      {call.invitees.map((i) => (
        <div key={`${i.email}-${i.name}`} className="truncate">
          {i.name ? <span className="font-medium">{i.name}</span> : null}
          {i.name && i.email ? ' · ' : ''}
          {i.email && <span className="text-gray-500 dark:text-gray-400">{i.email}</span>}
        </div>
      ))}
      <div className="text-gray-500 dark:text-gray-400">Advisor: {call.host?.name || 'Unassigned'}</div>
      {call.matchedBy && call.accountId && (
        <div className="text-xs text-gray-500 dark:text-gray-400">Matched to client by {MATCHED_BY_LABEL[call.matchedBy] || call.matchedBy}</div>
      )}
    </DetailBlock>

    <DetailBlock title="Booking">
      <div>{fmtWhen(call.start, call.timeKnown)}{call.durationMinutes ? ` · ${fmtMinutes(call.durationMinutes)}` : ''}</div>
      {call.bookedAt && <div className="text-gray-500 dark:text-gray-400">Booked {fmtShortDate(call.bookedAt)}</div>}
      {call.department && <div className="text-gray-500 dark:text-gray-400">{taskTimeDepartmentLabel(call.department)} department</div>}
      <div className="pt-1"><SourceChips sources={call.sources} /></div>
      {call.joinUrl && (
        <a href={call.joinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline dark:text-red-400">
          Meeting link <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </DetailBlock>

    <DetailBlock title="Implementation Hours">
      {call.log ? (
        <>
          <div>
            Logged {fmtHours(call.log.quantity)} on {call.log.dateKey || fmtShortDate(call.log.when)}
          </div>
          {call.log.meetingStatus && <div className="text-gray-500 dark:text-gray-400">Log status: {call.log.meetingStatus}</div>}
          {call.logStatus === LOG_STATUS.LOGGED_CANCELED && <div className="text-red-600 dark:text-red-400">{logExplanation(call)}</div>}
        </>
      ) : (
        <div className={call.logStatus === LOG_STATUS.NOT_LOGGED || call.logStatus === LOG_STATUS.UNMATCHED ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}>
          {logExplanation(call)}
        </div>
      )}
    </DetailBlock>

    {call.cancellation && (
      <DetailBlock title={call.status === CALL_STATUS.RESCHEDULED ? 'Rescheduled' : 'Canceled'}>
        <div>
          {call.status === CALL_STATUS.RESCHEDULED ? 'Moved' : 'Canceled'}
          {call.cancellation.by ? ` by ${call.cancellation.by}` : ''}
          {call.cancellation.byType ? ` (${call.cancellation.byType})` : ''}
          {call.cancellation.at ? ` on ${fmtShortDate(call.cancellation.at)}` : ''}
        </div>
        {call.cancellation.reason && <div className="text-gray-500 dark:text-gray-400">“{call.cancellation.reason}”</div>}
      </DetailBlock>
    )}

    {call.notes.length > 0 && (
      <DetailBlock title="Prep notes from invitee">
        {call.notes.map((n, i) => (
          <div key={i} className="whitespace-pre-line">{n.answer}</div>
        ))}
      </DetailBlock>
    )}

    {(call.recordingStatus === RECORDING_STATUS.NOT_RECORDED || call.recordingStatus === RECORDING_STATUS.PENDING) && (
      <DetailBlock title="Fathom recording">
        {call.recordingStatus === RECORDING_STATUS.PENDING ? (
          <div className="text-gray-500 dark:text-gray-400">This call just ended — Fathom usually sends the recording within an hour.</div>
        ) : (
          <>
            <div className="text-red-600 dark:text-red-400">No Fathom recording reached the CRM for this call.</div>
            {call.host?.fathomConnected === false && (
              <div className="text-gray-500 dark:text-gray-400">
                {call.host.name} hasn't connected Fathom in CRM ▸ My Profile, so their recordings can't come through.
              </div>
            )}
          </>
        )}
      </DetailBlock>
    )}

    {call.recording && (
      <DetailBlock title="Fathom recording">
        {call.recording.summary ? (
          <div className="max-h-48 overflow-auto whitespace-pre-line text-gray-600 dark:text-gray-300">{call.recording.summary}</div>
        ) : (
          <div className="text-gray-500 dark:text-gray-400">No summary yet</div>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
          {call.recording.actionItemCount > 0 && <span className="text-gray-500 dark:text-gray-400">{call.recording.actionItemCount} action items</span>}
          {call.recording.url && (
            <a href={call.recording.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-red-600 hover:underline dark:text-red-400">
              Watch recording <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </DetailBlock>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Source status
// ---------------------------------------------------------------------------

const SourceStatusBar = ({ sources, fetchedAt, onConnect }) => {
  if (!sources) return null;
  const { calendly, google, crm, fathom } = sources;
  const chip = (tone) => ({
    ok: 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300',
    warn: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
    error: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300',
    muted: 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300',
  }[tone]);

  let calendlyTone = 'ok';
  let calendlyText;
  let calendlyTitle = '';
  if (calendly.state === 'ok' && calendly.connectedUsers.length === 0 && calendly.failedUsers.length > 0) {
    calendlyTone = 'error';
    calendlyText = 'Calendly sign-in failed — reconnect';
    calendlyTitle = calendly.failedUsers.map((u) => `${u.name}: ${u.error}`).join('\n');
  } else if (calendly.state === 'ok') {
    calendlyText = calendly.mode === 'organization'
      ? `Calendly · whole team · ${calendly.eventCount} bookings`
      : `Calendly · ${calendly.connectedUsers.length} calendar${calendly.connectedUsers.length === 1 ? '' : 's'} · ${calendly.eventCount} bookings`;
    calendlyTitle = calendly.mode === 'organization'
      ? `Organization-wide via ${calendly.viaUser?.name || 'a Calendly admin'}`
      : `Connected: ${calendly.connectedUsers.map((u) => u.name).join(', ')}`;
    if (calendly.failedUsers.length) {
      calendlyTone = 'warn';
      calendlyTitle += ` — needs reconnecting: ${calendly.failedUsers.map((u) => u.name).join(', ')}`;
    }
  } else if (calendly.state === 'not_connected') {
    calendlyTone = 'warn';
    calendlyText = 'Calendly not connected';
    calendlyTitle = 'Connect Calendly in CRM ▸ My Profile ▸ Integrations';
  } else {
    calendlyTone = 'error';
    const notDeployed = calendly.code === 'functions/not-found' || calendly.code === 'functions/internal';
    calendlyText = notDeployed ? 'Calendly sync not deployed yet' : 'Calendly sync failed';
    calendlyTitle = calendly.message || '';
  }

  const googleTone = google.connectedUsers.length === 0 ? 'warn' : google.failedUsers.length ? 'warn' : 'ok';
  const googleText = google.connectedUsers.length === 0
    ? 'No team Google Calendars connected'
    : `Google Calendar · ${google.connectedUsers.length} of ${google.totalUsers} teammates`;
  const googleTitle = google.connectedUsers.length
    ? `Connected: ${google.connectedUsers.join(', ')}${google.failedUsers.length ? ` — couldn't load: ${google.failedUsers.join(', ')}` : ''}`
    : 'Teammates connect Google Calendar in CRM ▸ My Profile ▸ Integrations';

  const fathomTone = fathom.state !== 'ok' || fathom.connectedUsers.length === 0 ? 'warn' : 'ok';
  const fathomTitle = fathom.connectedUsers.length
    ? `Recordings come in from: ${fathom.connectedUsers.join(', ')}`
    : 'Teammates connect Fathom in CRM ▸ My Profile ▸ Integrations';

  const calendlyNeedsReconnect = calendly.state === 'ok' && calendly.failedUsers.length > 0;

  return (
    <div className="space-y-3">
    {calendlyNeedsReconnect && (
      <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        <div className="font-medium">
          Calendly couldn't sign in for {calendly.failedUsers.map((u) => u.name).join(', ')}
          {calendly.connectedUsers.length === 0 ? ' — no Calendly bookings are included below.' : '.'}
        </div>
        <div className="mt-1 text-xs opacity-80">{calendly.failedUsers[0]?.error}</div>
        <button type="button" onClick={onConnect} className="mt-2 rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">
          Reconnect Calendly in My Profile
        </button>
      </div>
    )}
    <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
      <span title={calendlyTitle} className={`${PILL} ${chip(calendlyTone)}`}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {calendlyText}
      </span>
      <span title={googleTitle} className={`${PILL} ${chip(googleTone)}`}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {googleText}
      </span>
      <span className={`${PILL} ${chip('muted')}`}>Hours logs · {crm.logCount}</span>
      <span title={fathomTitle} className={`${PILL} ${chip(fathomTone)}`}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {fathom.state === 'ok'
          ? `Fathom · ${fathom.recordingCount} recordings · ${fathom.connectedUsers.length} of ${fathom.totalUsers} teammates`
          : 'Fathom unavailable'}
      </span>
      {(calendly.state === 'not_connected' || google.connectedUsers.length === 0 || fathom.connectedUsers.length === 0) && (
        <button type="button" onClick={onConnect} className="font-medium text-red-600 hover:underline dark:text-red-400">
          Connect in My Profile
        </button>
      )}
      {fetchedAt && (
        <span className="text-gray-400 dark:text-gray-500">
          Synced {fetchedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </span>
      )}
    </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

const ComingUp = ({ calls, onOpenClient }) => {
  if (calls.length === 0) return null;
  return (
    <div className={`${CARD} p-5`}>
      <h2 className="mb-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
        Coming up · next {COMING_UP_DAYS} days
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {calls.map((call) => (
          <div key={call.id} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-800/50">
            <div className="flex w-12 shrink-0 flex-col items-center rounded-lg bg-white py-1.5 shadow-sm dark:bg-gray-900">
              <span className="text-[10px] font-semibold uppercase text-red-600 dark:text-red-400">
                {call.start.toLocaleDateString(undefined, { month: 'short' })}
              </span>
              <span className="text-lg font-bold leading-none text-gray-900 dark:text-white">{call.start.getDate()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <ClientCell call={call} onOpenClient={onOpenClient} />
              <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                {call.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · {call.title}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <Avatar host={call.host} size="h-5 w-5" />
                <span className="truncate text-xs text-gray-600 dark:text-gray-300">{call.host?.name || 'Unassigned'}</span>
                <span className="text-xs text-gray-400">· {relativeDays(call.start)}</span>
              </div>
            </div>
            <CallLinks call={call} />
          </div>
        ))}
      </div>
    </div>
  );
};

const CallsTable = ({ calls, onOpenClient }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => setVisible(PAGE_SIZE), [calls]);

  if (calls.length === 0) {
    return (
      <div className={`${CARD} py-16 text-center text-sm text-gray-500 dark:text-gray-400`}>
        No calls match these filters
      </div>
    );
  }

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/60">
            <tr>
              <th className={`${TH_CLASS} w-8`} />
              <th className={TH_CLASS}>When</th>
              <th className={TH_CLASS}>Client</th>
              <th className={TH_CLASS}>Call</th>
              <th className={TH_CLASS}>Advisor</th>
              <th className={TH_CLASS}>Status</th>
              <th className={TH_CLASS}>Recording</th>
              <th className={TH_CLASS}>Hours log</th>
              <th className={`${TH_CLASS} text-right`}>Links</th>
            </tr>
          </thead>
          <tbody>
            {calls.slice(0, visible).map((call) => {
              const open = expandedId === call.id;
              return (
                <React.Fragment key={call.id}>
                  <tr
                    onClick={() => setExpandedId(open ? null : call.id)}
                    className={`cursor-pointer border-b border-gray-100 transition-colors dark:border-gray-800 ${open ? 'bg-gray-50 dark:bg-gray-800/40' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}
                  >
                    <td className="pl-3 text-gray-400">
                      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="px-2 py-3 sm:px-4 whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtWhen(call.start, call.timeKnown)}</td>
                    <td className="max-w-[220px] px-2 py-3 sm:px-4"><ClientCell call={call} onOpenClient={onOpenClient} /></td>
                    <td className="max-w-[240px] px-2 py-3 sm:px-4">
                      <div className="truncate text-sm text-gray-900 dark:text-white">{call.title}</div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {[call.department ? taskTimeDepartmentLabel(call.department) : null, call.durationMinutes ? fmtMinutes(call.durationMinutes) : null]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </td>
                    <td className="max-w-[180px] px-2 py-3 sm:px-4"><HostCell host={call.host} /></td>
                    <td className="px-2 py-3 sm:px-4"><StatusPill status={call.status} /></td>
                    <td className="px-2 py-3 sm:px-4"><RecordingPill call={call} /></td>
                    <td className="px-2 py-3 sm:px-4"><LogPill call={call} /></td>
                    <td className="px-2 py-3 sm:px-4"><CallLinks call={call} /></td>
                  </tr>
                  {open && (
                    <tr className="border-b border-gray-100 bg-gray-50/70 dark:border-gray-800 dark:bg-gray-800/30">
                      <td />
                      <td colSpan={8} className="px-2 py-5 sm:px-4">
                        <CallDetail call={call} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {calls.length > visible && (
        <div className="border-t border-gray-100 py-3 text-center dark:border-gray-800">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Show more ({calls.length - visible} left)
          </button>
        </div>
      )}
    </div>
  );
};

const AdvisorTable = ({ rows }) => {
  if (rows.length === 0) {
    return <div className={`${CARD} py-16 text-center text-sm text-gray-500 dark:text-gray-400`}>No calls match these filters</div>;
  }
  const num = 'px-2 sm:px-4 py-3 text-center tabular-nums';
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/60">
            <tr>
              <th className={TH_CLASS}>Advisor</th>
              {['Calls', 'Completed', 'Upcoming', 'Canceled', 'Rescheduled', 'No-show', 'Clients', 'Hours held', 'Avg call', 'Held rate', 'Recorded', 'Not recorded', 'Record rate'].map((h) => (
                <th key={h} className={`${TH_CLASS} text-center`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                <td className="px-2 py-3 sm:px-4">
                  <HostCell host={row.host} />
                  {row.host?.fathomConnected === false && <div className="mt-1 pl-9"><FathomNotConnectedPill /></div>}
                </td>
                <td className={`${num} font-semibold text-gray-900 dark:text-white`}>{row.total}</td>
                <td className={`${num} text-green-600 dark:text-green-400`}>{row.completed}</td>
                <td className={`${num} text-blue-600 dark:text-blue-400`}>{row.upcoming}</td>
                <td className={`${num} text-red-600 dark:text-red-400`}>{row.canceled}</td>
                <td className={`${num} text-amber-600 dark:text-amber-400`}>{row.rescheduled}</td>
                <td className={`${num} text-orange-600 dark:text-orange-400`}>{row.noShow}</td>
                <td className={`${num} text-gray-700 dark:text-gray-300`}>{row.clientCount}</td>
                <td className={`${num} text-gray-700 dark:text-gray-300`}>{row.heldMinutes ? fmtHours(row.heldMinutes / 60) : '—'}</td>
                <td className={`${num} text-gray-700 dark:text-gray-300`}>{fmtMinutes(row.avgMinutes)}</td>
                <td className={`${num} text-gray-700 dark:text-gray-300`}>{row.heldRate == null ? '—' : `${row.heldRate}%`}</td>
                <td className={`${num} text-violet-600 dark:text-violet-400`}>{row.recorded}</td>
                <td className={`${num} ${row.notRecorded ? 'font-semibold text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{row.notRecorded}</td>
                <td className={`${num} ${row.recordingRate != null && row.recordingRate < 100 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {row.recordingRate == null ? '—' : `${row.recordingRate}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ClientTable = ({ rows, onOpenClient }) => {
  if (rows.length === 0) {
    return <div className={`${CARD} py-16 text-center text-sm text-gray-500 dark:text-gray-400`}>No clients match these filters</div>;
  }
  const num = 'px-2 sm:px-4 py-3 text-center tabular-nums';
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/60">
            <tr>
              <th className={TH_CLASS}>Client</th>
              {['Completed', 'Upcoming', 'Canceled', 'Hours held', 'Hours logged', 'Last call', 'Next call'].map((h) => (
                <th key={h} className={`${TH_CLASS} text-center`}>{h}</th>
              ))}
              <th className={TH_CLASS}>Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const cold = !row.nextCall && (row.daysSinceLastCall == null || row.daysSinceLastCall > 30);
              return (
                <tr key={row.accountId} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                  <td className="max-w-[240px] px-2 py-3 sm:px-4">
                    <button
                      type="button"
                      onClick={() => onOpenClient(row.accountId)}
                      className="block max-w-full truncate text-left font-semibold text-gray-900 hover:text-red-600 dark:text-white dark:hover:text-red-400"
                    >
                      {row.accountName || 'Client'}
                    </button>
                  </td>
                  <td className={`${num} text-green-600 dark:text-green-400`}>{row.completed}</td>
                  <td className={`${num} text-blue-600 dark:text-blue-400`}>{row.upcoming}</td>
                  <td className={`${num} text-red-600 dark:text-red-400`}>{row.canceled + row.rescheduled + row.noShow}</td>
                  <td className={`${num} text-gray-700 dark:text-gray-300`}>{row.heldMinutes ? fmtHours(row.heldMinutes / 60) : '—'}</td>
                  <td className={`${num} text-gray-700 dark:text-gray-300`}>{row.loggedHours ? fmtHours(row.loggedHours) : '—'}</td>
                  <td className={`${num} text-gray-700 dark:text-gray-300`}>
                    {row.lastCall ? (
                      <>
                        <div>{fmtShortDate(row.lastCall)}</div>
                        <div className="text-xs text-gray-400">{row.daysSinceLastCall}d ago</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className={`${num} text-gray-700 dark:text-gray-300`}>
                    {row.nextCall ? (
                      <>
                        <div>{fmtShortDate(row.nextCall)}</div>
                        <div className="text-xs text-gray-400">{relativeDays(row.nextCall)}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-3 sm:px-4">
                    <div className="flex flex-wrap gap-1.5">
                      {cold && (
                        <span className={`${PILL} ${STATUS_META[CALL_STATUS.CANCELED].cls}`}>
                          {row.lastCall ? 'No call in 30+ days' : 'No calls on record'}
                        </span>
                      )}
                      {row.attention > 0 && (
                        <span className={`${PILL} bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800`}>
                          {row.attention} to review
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ATTENTION_GROUPS = [
  {
    id: 'not_logged',
    title: 'Held but not logged to Implementation Hours',
    hint: 'No hours were deducted for these calls. Log them from the client\'s Program Deliverables.',
    test: (c) => c.logStatus === LOG_STATUS.NOT_LOGGED && c.status === CALL_STATUS.COMPLETED,
  },
  {
    id: 'not_recorded',
    title: 'Held but not recorded in Fathom',
    hint: 'Every call should be recorded. Make sure the advisor starts Fathom on every call and has Fathom connected in My Profile.',
    test: (c) => c.recordingStatus === RECORDING_STATUS.NOT_RECORDED,
  },
  {
    id: 'logged_canceled',
    title: 'Canceled or rescheduled but still logged',
    hint: 'These log entries still count against the client\'s hours. Remove them from Program Deliverables.',
    test: (c) => c.logStatus === LOG_STATUS.LOGGED_CANCELED,
  },
  {
    id: 'unmatched',
    title: 'Couldn\'t match to a client',
    hint: 'Add the invitee as a portal user or contact on the right client so their calls attach automatically.',
    test: (c) => c.logStatus === LOG_STATUS.UNMATCHED,
  },
];

const AttentionPanel = ({ calls, onOpenClient }) => {
  const groups = ATTENTION_GROUPS.map((g) => ({ ...g, calls: calls.filter(g.test) })).filter((g) => g.calls.length > 0);
  if (groups.length === 0) {
    return (
      <div className={`${CARD} flex flex-col items-center gap-2 py-16 text-center`}>
        <CheckCircle2 className="h-8 w-8 text-green-500" />
        <div className="text-sm font-medium text-gray-900 dark:text-white">Everything reconciles</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">Every call in this range matches the Implementation Hours log and has a Fathom recording.</div>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.id} className={`${CARD} overflow-hidden`}>
          <div className="border-b border-gray-100 px-5 py-4 text-center dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {group.title} <span className="text-gray-400">({group.calls.length})</span>
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{group.hint}</p>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {group.calls.map((call) => (
              <li key={call.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
                <div className="w-40 shrink-0 text-sm text-gray-700 dark:text-gray-300">{fmtWhen(call.start, call.timeKnown)}</div>
                <div className="min-w-[160px] flex-1"><ClientCell call={call} onOpenClient={onOpenClient} /></div>
                <div className="min-w-[140px] flex-1 truncate text-sm text-gray-600 dark:text-gray-400">{call.title}</div>
                <div className="w-40"><HostCell host={call.host} /></div>
                <StatusPill status={call.status} />
                {group.id === 'not_recorded' && call.host?.fathomConnected === false && <FathomNotConnectedPill />}
                {call.log && <span className="text-xs text-gray-500 dark:text-gray-400">Logged {fmtHours(call.log.quantity)} · {call.log.dateKey}</span>}
                {call.accountId && (
                  <button
                    type="button"
                    onClick={() => onOpenClient(call.accountId)}
                    className="ml-auto inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Open client <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'calls', label: 'Calls' },
  { id: 'advisors', label: 'By Advisor' },
  { id: 'clients', label: 'By Client' },
  { id: 'attention', label: 'Needs Attention' },
];

const CallReports = () => {
  const navigate = useNavigate();
  const [internalUsers, setInternalUsers] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('last_90');
  const [tab, setTab] = useState('calls');
  const [search, setSearch] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('implementation');
  const [recordingFilter, setRecordingFilter] = useState('all');
  const requestRef = useRef(0);

  useEffect(() => {
    getDocs(collection(db, 'internal_users'))
      .then((snap) => setInternalUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => {
        console.error('Call Reports: failed to load internal users', err);
        setInternalUsers([]);
      });
  }, []);

  const load = useCallback(async () => {
    if (!internalUsers) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const fetchWindow = getFetchWindow(getTimeframeRange(timeframe, now), now);
      const result = await loadCallReport({ ...fetchWindow, internalUsers });
      if (requestId === requestRef.current) setReport(result);
    } catch (err) {
      console.error('Call Reports: load failed', err);
      if (requestId === requestRef.current) setError(err?.message || 'Could not load call reports');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [internalUsers, timeframe]);

  useEffect(() => {
    load();
  }, [load]);

  // Anchor rolling ranges to the sync time so filtering matches what was fetched.
  const syncedAt = report?.fetchedAt || null;
  const range = useMemo(() => getTimeframeRange(timeframe, syncedAt || new Date()), [timeframe, syncedAt]);
  const rangeIsPast = range.end <= (syncedAt || new Date());

  const advisorOptions = useMemo(
    () => (internalUsers || [])
      .map((u) => ({ id: u.id, name: internalUserName(u) }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [internalUsers]
  );

  // Every filter except timeframe + status (Coming up ignores both).
  const baseCalls = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.calls.filter((call) => {
      if (typeFilter === 'implementation' && call.kind !== 'implementation') return false;
      if (advisorFilter !== 'all') {
        const hostId = call.host?.id || null;
        if (advisorFilter === 'unassigned' ? !!hostId : hostId !== advisorFilter) return false;
      }
      if (departmentFilter !== 'all') {
        if (departmentFilter === 'none' ? !!call.department : call.department !== departmentFilter) return false;
      }
      if (q) {
        const haystack = [call.accountName, call.invitee?.name, call.invitee?.email, call.title, call.host?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [report, search, typeFilter, advisorFilter, departmentFilter]);

  const rangeCalls = useMemo(
    () => baseCalls.filter((c) => c.start >= range.start && c.start < range.end),
    [baseCalls, range]
  );

  // A past-only timeframe has nothing upcoming in it, so "Upcoming" falls back to the
  // next 30 days (every fetch covers them).
  const upcomingCalls = useMemo(() => {
    const pool = rangeIsPast ? baseCalls.filter((c) => c.start >= range.end) : rangeCalls;
    return pool.filter((c) => c.status === CALL_STATUS.UPCOMING).sort((a, b) => a.start - b.start);
  }, [baseCalls, rangeCalls, range, rangeIsPast]);

  const visibleCalls = useMemo(() => {
    let calls;
    if (statusFilter === 'all') calls = rangeCalls;
    else if (statusFilter === 'attention') calls = rangeCalls.filter(needsAttention);
    else if (statusFilter === CALL_STATUS.UPCOMING) calls = upcomingCalls;
    else calls = rangeCalls.filter((c) => c.status === statusFilter);
    return recordingFilter === 'all' ? calls : calls.filter((c) => c.recordingStatus === recordingFilter);
  }, [rangeCalls, upcomingCalls, statusFilter, recordingFilter]);

  const comingUp = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + COMING_UP_DAYS * DAY_MS);
    return baseCalls
      .filter((c) => c.status === CALL_STATUS.UPCOMING && c.start >= now && c.start < horizon)
      .sort((a, b) => a.start - b.start)
      .slice(0, 6);
  }, [baseCalls]);

  const stats = useMemo(
    () => ({ ...summarizeCalls(rangeCalls), upcoming: upcomingCalls.length }),
    [rangeCalls, upcomingCalls]
  );
  const advisorRows = useMemo(() => groupCallsByAdvisor(visibleCalls), [visibleCalls]);
  const clientRows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    const includeIdleClients = advisorFilter === 'all' && departmentFilter === 'all' && statusFilter === 'all' && recordingFilter === 'all';
    return groupCallsByClient(visibleCalls, { clients: report.clients, allLogs: report.allLogs, includeIdleClients })
      .filter((row) => !q || String(row.accountName || '').toLowerCase().includes(q) || visibleCalls.some((c) => c.accountId === row.accountId))
      .sort((a, b) => {
        const coldA = !a.nextCall && (a.daysSinceLastCall == null || a.daysSinceLastCall > 30);
        const coldB = !b.nextCall && (b.daysSinceLastCall == null || b.daysSinceLastCall > 30);
        if (coldA !== coldB) return coldA ? -1 : 1;
        return (b.daysSinceLastCall ?? Infinity) - (a.daysSinceLastCall ?? Infinity);
      });
  }, [report, visibleCalls, search, advisorFilter, departmentFilter, statusFilter, recordingFilter]);

  const openClient = useCallback((accountId) => navigate(`/crm/client/${accountId}`), [navigate]);
  const toggleStatus = (status) => setStatusFilter((s) => (s === status ? 'all' : status));
  const toggleRecording = (status) => setRecordingFilter((s) => (s === status ? 'all' : status));
  const hasFilters = search || advisorFilter !== 'all' || departmentFilter !== 'all' || statusFilter !== 'all' || typeFilter !== 'implementation' || recordingFilter !== 'all';

  const initialLoading = loading && !report;

  return (
    <div className="min-h-screen p-4 md:p-6 relative text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4">
          <BackButton to="/crm/reports" />
          <div className="text-center min-w-0 px-2">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Call Reports</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Implementation Hours calls from Calendly, Google Calendar, Fathom and the hours log
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{loading ? 'Syncing…' : 'Sync'}</span>
          </button>
        </div>

        <SourceStatusBar sources={report?.sources} fetchedAt={report?.fetchedAt} onConnect={() => navigate('/crm/my-profile')} />

        {/* Filters */}
        <div className={`${CARD} p-4`}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_repeat(6,minmax(0,1fr))]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client, invitee, advisor…"
                className={`${FIELD_CLASS} pl-10 pr-9`}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <FilterSelect icon={false} value={timeframe} onChange={(e) => setTimeframe(e.target.value)} aria-label="Timeframe">
              {TIMEFRAMES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </FilterSelect>
            <FilterSelect value={advisorFilter} onChange={(e) => setAdvisorFilter(e.target.value)} aria-label="Advisor">
              <option value="all">All advisors</option>
              {advisorOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              <option value="unassigned">Unassigned</option>
            </FilterSelect>
            <FilterSelect value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} aria-label="Department">
              <option value="all">All departments</option>
              {TASK_TIME_DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              <option value="none">No department</option>
            </FilterSelect>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              {Object.entries(STATUS_META).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
              <option value="attention">Needs attention</option>
            </FilterSelect>
            <FilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Call type">
              <option value="implementation">Implementation calls</option>
              <option value="all">All client meetings</option>
            </FilterSelect>
            <FilterSelect value={recordingFilter} onChange={(e) => setRecordingFilter(e.target.value)} aria-label="Recording">
              <option value="all">All recordings</option>
              <option value={RECORDING_STATUS.RECORDED}>Recorded</option>
              <option value={RECORDING_STATUS.NOT_RECORDED}>Not recorded</option>
              <option value={RECORDING_STATUS.PENDING}>Processing</option>
            </FilterSelect>
          </div>
          {hasFilters && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setAdvisorFilter('all');
                  setDepartmentFilter('all');
                  setStatusFilter('all');
                  setTypeFilter('implementation');
                  setRecordingFilter('all');
                }}
                className="text-xs font-medium text-red-600 hover:underline dark:text-red-400"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        {initialLoading ? (
          <div className={`${CARD} flex flex-col items-center justify-center gap-4 py-20`}>
            <BBALoader size={88} />
            <p className="text-sm text-gray-500 dark:text-gray-400">Syncing Calendly, Google Calendar and hours logs…</p>
          </div>
        ) : report && (
          <>
            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">
              <CrmStatCard icon={Phone} label="Total Calls" value={stats.total} tone="gray" compact onClick={() => setStatusFilter('all')} active={statusFilter === 'all'} />
              <CrmStatCard icon={CheckCircle2} label="Completed" value={stats.completed} tone="green" compact onClick={() => toggleStatus(CALL_STATUS.COMPLETED)} active={statusFilter === CALL_STATUS.COMPLETED} />
              <CrmStatCard icon={CalendarClock} label={rangeIsPast ? 'Next 30 Days' : 'Upcoming'} value={stats.upcoming} tone="blue" compact onClick={() => toggleStatus(CALL_STATUS.UPCOMING)} active={statusFilter === CALL_STATUS.UPCOMING} />
              <CrmStatCard icon={XCircle} label="Canceled" value={stats.canceled} tone="red" compact onClick={() => toggleStatus(CALL_STATUS.CANCELED)} active={statusFilter === CALL_STATUS.CANCELED} />
              <CrmStatCard icon={Repeat} label="Rescheduled" value={stats.rescheduled} tone="yellow" compact onClick={() => toggleStatus(CALL_STATUS.RESCHEDULED)} active={statusFilter === CALL_STATUS.RESCHEDULED} />
              <CrmStatCard icon={UserX} label="No-shows" value={stats.noShow} tone="orange" compact onClick={() => toggleStatus(CALL_STATUS.NO_SHOW)} active={statusFilter === CALL_STATUS.NO_SHOW} />
              <CrmStatCard icon={Clock} label="Hours Held" value={fmtHours(stats.heldMinutes / 60)} tone="gray" compact />
              <CrmStatCard icon={VideoOff} label="Not Recorded" value={stats.notRecorded} tone={stats.notRecorded ? 'red' : 'gray'} compact onClick={() => toggleRecording(RECORDING_STATUS.NOT_RECORDED)} active={recordingFilter === RECORDING_STATUS.NOT_RECORDED} />
              <CrmStatCard icon={AlertTriangle} label="Needs Attention" value={stats.attention} tone={stats.attention ? 'orange' : 'gray'} compact onClick={() => setTab('attention')} active={tab === 'attention'} />
            </div>
            <p className="-mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
              {stats.heldRate == null ? 'No completed or canceled calls yet' : `${stats.heldRate}% held`}
              {stats.avgMinutes ? ` · avg call ${fmtMinutes(stats.avgMinutes)}` : ''}
              {stats.recordingRate != null ? ` · ${stats.recordingRate}% recorded (${stats.recorded} of ${stats.recorded + stats.notRecorded})` : ''}
              {` · ${fmtHours(stats.loggedHours)} on the Implementation Hours log`}
              {loading && ' · refreshing…'}
            </p>

            {/* Tabs */}
            <div className="flex justify-center">
              <div className="inline-flex flex-wrap justify-center gap-1 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                      tab === t.id
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    {t.label}
                    {t.id === 'attention' && stats.attention > 0 && (
                      <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] ${tab === t.id ? 'bg-white/20' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'}`}>
                        {stats.attention}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {tab === 'calls' && (
              <div className="space-y-6">
                {statusFilter === 'all' && <ComingUp calls={comingUp} onOpenClient={openClient} />}
                <CallsTable calls={visibleCalls} onOpenClient={openClient} />
              </div>
            )}
            {tab === 'advisors' && <AdvisorTable rows={advisorRows} />}
            {tab === 'clients' && <ClientTable rows={clientRows} onOpenClient={openClient} />}
            {tab === 'attention' && <AttentionPanel calls={rangeCalls} onOpenClient={openClient} />}
          </>
        )}
      </div>
    </div>
  );
};

export default CallReports;
